import React, { useMemo, useState } from 'react';
import { useLms } from '../../context/LmsContext';
import { Card, DataTable, FormRow, MiniBar, PageHeader, StatusBadge } from '../../components/ui';
import { CourseGradingSheet, ProgramGradingSheet } from '../../components/GradingSheets';
import { assignmentGrade, byId, derivedAssignmentStatus, fullName, getAssignmentProgress, getCourse, getQuizzesForVersion, isOverdue } from '../../utils/lmsSelectors';

function downloadCsv(filename, rows) {
  const headers = Object.keys(rows[0] || {});
  const csv = [headers.join(','), ...rows.map((row) => headers.map((header) => JSON.stringify(row[header] ?? '')).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export default function AdminReports() {
  const { data } = useLms();
  const [status, setStatus] = useState('all');
  const [gradeCourseId, setGradeCourseId] = useState(data.courses[0]?.id || '');
  const [gradeProgramId, setGradeProgramId] = useState((data.programs || [])[0]?.id || '');
  const rows = useMemo(() => data.assignments.map((assignment) => {
    const learner = byId(data.users, assignment.assignedToUserId);
    const course = getCourse(data, assignment.courseId);
    const progress = getAssignmentProgress(data, assignment);
    return {
      id: assignment.id,
      learner: fullName(learner),
      team: learner?.team,
      course: course?.title,
      category: course?.category,
      dueDate: assignment.dueDate,
      status: derivedAssignmentStatus(data, assignment),
      progress: progress.percent,
      grade: assignmentGrade(data, assignment),
      overdue: isOverdue(assignment) ? 'Yes' : 'No'
    };
  }), [data]);
  const filteredRows = status === 'all' ? rows : rows.filter((row) => row.status === status);
  const statusBuckets = ['not_started', 'in_progress', 'completed', 'overdue', 'needs_review'];

  return (
    <>
      <PageHeader title="Reports" eyebrow="Admin" description="Completion, overdue, grade, certificate, and training matrix reports using Supabase-backed LMS data." />
      <div className="grid two">
        <Card>
          <h2>Completion status mix</h2>
          {statusBuckets.map((bucket) => {
            const count = rows.filter((row) => row.status === bucket).length;
            const value = rows.length ? Math.round((count / rows.length) * 100) : 0;
            return <MiniBar key={bucket} label={`${bucket.replace(/_/g, ' ')} (${count})`} value={value} />;
          })}
        </Card>
        <Card>
          <h2>Course effectiveness</h2>
          {data.courses.filter((course) => course.status === 'published').map((course) => {
            const assigned = data.assignments.filter((assignment) => assignment.courseId === course.id);
            const completed = assigned.filter((assignment) => assignment.status === 'completed').length;
            const rate = assigned.length ? Math.round((completed / assigned.length) * 100) : 0;
            return <MiniBar key={course.id} label={`${course.title} (${assigned.length})`} value={rate} />;
          })}
        </Card>
      </div>
      <Card>
        <div className="toolbar between">
          <label>Status <select value={status} onChange={(e) => setStatus(e.target.value)}><option value="all">All</option>{statusBuckets.map((bucket) => <option key={bucket} value={bucket}>{bucket.replace(/_/g, ' ')}</option>)}</select></label>
          <button className="button button-secondary" onClick={() => downloadCsv('lms-completion-report.csv', filteredRows)}>Export CSV</button>
        </div>
        <DataTable
          rows={filteredRows}
          columns={[
            { key: 'learner', label: 'Learner' },
            { key: 'team', label: 'Team' },
            { key: 'course', label: 'Course' },
            { key: 'category', label: 'Category' },
            { key: 'status', label: 'Status', render: (row) => <StatusBadge status={row.status} /> },
            { key: 'progress', label: 'Progress', render: (row) => `${row.progress}%` },
            { key: 'grade', label: 'Grade', render: (row) => row.grade === null ? '—' : `${row.grade}%` },
            { key: 'dueDate', label: 'Due date' }
          ]}
        />
      </Card>
      <div className="grid two">
        <Card>
          <h2>Course grading sheet</h2>
          <FormRow label="Select course"><select value={gradeCourseId} onChange={(e) => setGradeCourseId(e.target.value)}>{data.courses.map((course) => <option key={course.id} value={course.id}>{course.title}</option>)}</select></FormRow>
          <CourseGradingSheet data={data} courseId={gradeCourseId} />
        </Card>
        <Card>
          <h2>Program grading sheet</h2>
          <FormRow label="Select program"><select value={gradeProgramId} onChange={(e) => setGradeProgramId(e.target.value)}>{(data.programs || []).map((program) => <option key={program.id} value={program.id}>{program.title}</option>)}</select></FormRow>
          <ProgramGradingSheet data={data} programId={gradeProgramId} />
        </Card>
      </div>
      <Card>
        <h2>Question coverage</h2>
        <DataTable
          rows={data.quizzes.map((quiz) => {
            const course = data.courses.find((item) => item.currentVersionId === quiz.courseVersionId);
            return { id: quiz.id, course: course?.title || 'Draft course', quiz: quiz.title, questions: quiz.questions.length, pass: `${quiz.passingScore}%`, attempts: quiz.maxAttempts, required: quiz.isRequired ? 'Yes' : 'No', versionQuizzes: getQuizzesForVersion(data, quiz.courseVersionId).length };
          })}
          columns={[
            { key: 'course', label: 'Course' },
            { key: 'quiz', label: 'Quiz' },
            { key: 'questions', label: 'Questions' },
            { key: 'pass', label: 'Pass score' },
            { key: 'attempts', label: 'Attempts' },
            { key: 'required', label: 'Required' }
          ]}
        />
      </Card>
    </>
  );
}
